// ============================================================================
// src/components/BenchmarkPanel/BenchmarkPanel.tsx
// PV-1052 · PR-G — Live DLD market comparison panel for the public DealPage.
//
// Consumes the `get_deal_dld_comparison(UUID)` RPC via `useDealDldComparison`
// hook. Dispatches the layout based on the cascade outcome :
//   - loading             → skeleton with reserved height
//   - error / not_found   → hidden (defensive — should never happen on a
//                            public deal page that already filters status)
//   - off_plan            → reduced layout : original_price reference only
//   - insufficient_data   → reduced layout : asking_price + helper text
//   - cascade scope       → full layout : kicker + 4 rows + distribution chart
//                           + comparables collapsible + footer
//
// The deal's `asking_price` is read from a small prop (not refetched here)
// so the panel can render quickly once the parent DealPage already has the
// deal in hand.
// ============================================================================

import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { useDealDldComparison } from '@/hooks/useDealDldComparison';
import type { DldComparisonResponse, DldComparisonScope } from '@/lib/db/dld-comparison';
import { formatAed, formatNumber, formatPricePerSqft } from '@/lib/format';
import { DistributionChart } from './DistributionChart';
import { ComparableTransactionsTable } from './ComparableTransactionsTable';
import './BenchmarkPanel.css';

/** Format a percentage with 1 decimal: 16.67 → "16.7", 25 → "25.0". */
function formatPercent(value: number): string {
  return value.toFixed(1);
}

export type BenchmarkPanelProps = {
  /** UUID of the deal — used as RPC argument. */
  dealId: string;
  /** Asking price from the parent DealPage (avoids redundant DB fetch). */
  askingPrice: number;
  /** Community label — named in the Community benchmark section. */
  community: string;
  /** Building label — named in the Building/Project benchmark section. NULL
   *  when the deal is not mapped to a specific building. For a project-group
   *  scope this carries the marketed project name (the deal's free text). */
  building: string | null;
  /** Zone label — named in the Zone benchmark section (ADR-055 zone levels). */
  zone: string;
};

// ADR-055 — the EXACT perimeter family of the winning cohort. Classified by
// FAMILY prefix, not by a frozen set of window-specific literals: the RPC
// builds windowed scopes dynamically (`same_building_<N>m`) from the Settings
// M12 config. Special scopes (off_plan / insufficient_data / not_found) are
// handled by the caller before this classification.
type ScopeFamily = 'building' | 'project_group' | 'community' | 'broader' | 'zone' | 'zone_broader';

function scopeFamily(scope: DldComparisonScope): ScopeFamily | null {
  if (scope.startsWith('same_building_')) return 'building';
  if (scope.startsWith('same_project_group_')) return 'project_group';
  if (scope.startsWith('same_community_')) return 'community';
  if (scope === 'broader_market') return 'broader';
  if (scope.startsWith('same_zone_')) return 'zone';
  if (scope === 'zone_broader') return 'zone_broader';
  return null;
}

/** Confidence-line qualifier key per scope family (exact-perimeter wording). */
const CONFIDENCE_DETAIL_KEY: Record<ScopeFamily, string> = {
  building: 'common:benchmark.confidence_detail.same_building',
  project_group: 'common:benchmark.confidence_detail.same_project_group',
  community: 'common:benchmark.confidence_detail.same_community',
  broader: 'common:benchmark.confidence_detail.broader_market',
  zone: 'common:benchmark.confidence_detail.same_zone',
  zone_broader: 'common:benchmark.confidence_detail.zone_broader',
};

export function BenchmarkPanel({
  dealId,
  askingPrice,
  community,
  building,
  zone,
}: BenchmarkPanelProps) {
  const { t } = useTranslation();
  const state = useDealDldComparison(dealId);

  // Loading skeleton — reserved height to avoid layout shift (memory `scroll-anchoring-async-fetch`)
  if (state.status === 'loading') {
    return (
      <div className="benchmark-panel benchmark-panel--loading" aria-busy="true" aria-live="polite">
        <div className="benchmark-panel__head">
          <div className="benchmark-panel__kicker">{t('common:benchmark.kicker.loading')}</div>
        </div>
        <div className="benchmark-panel__skeleton-row" />
        <div className="benchmark-panel__skeleton-row" />
        <div className="benchmark-panel__skeleton-row" />
        <div className="benchmark-panel__skeleton-row" />
        <div className="benchmark-panel__skeleton-chart" />
      </div>
    );
  }

  // Error or not_found → hidden (defensive)
  if (state.status === 'error') {
    if (typeof console !== 'undefined') {
      console.error('[BenchmarkPanel] RPC failed, panel hidden:', state.error.message);
    }
    return null;
  }

  const data = state.data;

  if (data.scope === 'not_found') {
    return null;
  }

  // Off-plan reduced layout — show % discount vs original price (Hani 2026-05-23)
  if (data.scope === 'off_plan') {
    const originalPrice = data.reference_price;
    const hasOriginal = originalPrice !== null && originalPrice > 0;
    // % off the original price: positive = below original (good for buyer)
    const offPlanPct = hasOriginal ? ((originalPrice - askingPrice) / originalPrice) * 100 : null;
    const offPlanDiffAed = hasOriginal ? originalPrice - askingPrice : null;
    const offPlanTone: 'positive' | undefined =
      offPlanPct !== null && offPlanPct > 0 ? 'positive' : undefined;
    const offPlanText =
      offPlanPct === null || offPlanDiffAed === null
        ? '—'
        : offPlanPct >= 0
          ? t('common:benchmark.rows.off_plan_savings', {
              pct: formatPercent(offPlanPct),
              amount: formatNumber(Math.round(Math.abs(offPlanDiffAed))),
            })
          : t('common:benchmark.rows.off_plan_premium', {
              pct: formatPercent(Math.abs(offPlanPct)),
              amount: formatNumber(Math.round(Math.abs(offPlanDiffAed))),
            });

    return (
      <div className="benchmark-panel benchmark-panel--reduced">
        <div className="benchmark-panel__head">
          <div className="benchmark-panel__kicker">{t('common:benchmark.kicker.off_plan')}</div>
        </div>
        <BenchmarkRow
          label={t('common:benchmark.rows.original_price')}
          value={hasOriginal ? formatAed(originalPrice) : '—'}
          tone="data"
        />
        <BenchmarkRow
          label={t('common:benchmark.rows.asking_price')}
          value={formatAed(askingPrice)}
        />
        {hasOriginal && (
          <BenchmarkRow
            label={t('common:benchmark.rows.off_plan_discount')}
            value={offPlanText}
            tone={offPlanTone}
          />
        )}
      </div>
    );
  }

  // Insufficient data reduced layout — Spec V1.1 §M4 L692 + UI Behavior §5.3:
  // the comparison table is replaced by an explicit warning banner (ADR-047).
  if (data.scope === 'insufficient_data') {
    return (
      <div className="benchmark-panel benchmark-panel--reduced">
        <div className="benchmark-panel__head">
          <div className="benchmark-panel__kicker">{t('common:benchmark.kicker.insufficient')}</div>
        </div>
        <div className="benchmark-panel__warning" role="note">
          {t('common:benchmark.warning.insufficient_banner')}
        </div>
        <BenchmarkRow
          label={t('common:benchmark.rows.asking_price')}
          value={formatAed(askingPrice)}
        />
      </div>
    );
  }

  // Cascade scope — full layout
  const family = scopeFamily(data.scope);
  if (family === null) {
    // Defensive: unknown scope (shouldn't happen if RPC contract is respected)
    return null;
  }

  return (
    <BenchmarkPanelNominal
      data={data}
      family={family}
      askingPrice={askingPrice}
      community={community}
      building={building}
      zone={zone}
    />
  );
}

// ---------------------------------------------------------------------------
// Nominal layout (extracted for readability — only reached when all stats are
// guaranteed non-null by the RPC contract for cascade scopes).
// ---------------------------------------------------------------------------

type NominalProps = {
  data: DldComparisonResponse;
  family: ScopeFamily;
  askingPrice: number;
  community: string;
  building: string | null;
  zone: string;
};

// ADR-055 — explicit-perimeter layout: the winning cohort fills its named
// section (Building / Project / Community / Zone benchmark) so the buyer
// always reads WHICH level the comparison is made at. When the cohort is a
// fallback level, the building section renders an "Insufficient data"
// placeholder for transparency.
function BenchmarkPanelNominal({
  data,
  family,
  askingPrice,
  community,
  building,
  zone,
}: NominalProps) {
  const { t } = useTranslation();

  // Type-narrow : cascade scope guarantees these are non-null per RPC SQL contract
  const median = data.median_price_per_sqft ?? 0;
  const mean = data.average_price_per_sqft ?? 0;
  const central = data.display_stat === 'mean' ? mean : median;
  const dealPps = data.deal_price_per_sqft ?? 0;
  const period = data.period_months ?? 0;
  const sample = data.sample_size ?? 0;
  const est = data.estimated_market_value ?? 0;
  const discountAed = data.discount_aed ?? 0;
  const discountPct = data.discount_percent ?? 0;

  const isBuildingScope = family === 'building';
  const isProjectScope = family === 'project_group';
  const isCommunityScope = family === 'community' || family === 'broader';
  const isZoneScope = family === 'zone' || family === 'zone_broader';

  const confidenceKey = `common:benchmark.confidence.${data.confidence ?? 'low'}`;
  const buildingName = building ?? t('common:benchmark.this_building');
  // Winning-section label: the perimeter the stats actually come from.
  const winningName = isBuildingScope
    ? buildingName
    : isProjectScope
      ? buildingName // marketed project name = the deal's building free text
      : isCommunityScope
        ? community
        : zone;
  const footerStatKey =
    data.display_stat === 'mean'
      ? 'common:benchmark.footer.stat_mean'
      : 'common:benchmark.footer.stat_median';

  // ADR-055 — every positive discount is surfaced with the positive tone,
  // whatever the perimeter: the section title + confidence line carry the
  // exact-perimeter transparency (no more neutral community gating).
  const discountTone: 'positive' | 'data' | undefined = discountPct > 0 ? 'positive' : undefined;
  const discountText =
    discountPct >= 0
      ? t('common:benchmark.rows.savings', {
          pct: formatPercent(Math.abs(discountPct)),
          amount: formatNumber(Math.round(Math.abs(discountAed))),
        })
      : t('common:benchmark.rows.above_market', {
          pct: formatPercent(Math.abs(discountPct)),
          amount: formatNumber(Math.round(Math.abs(discountAed))),
        });

  return (
    <div className="benchmark-panel">
      <div className="benchmark-panel__head">
        <div className="benchmark-panel__kicker">{t('common:benchmark.title')}</div>
      </div>

      {/* Building benchmark section — filled when winning, placeholder otherwise */}
      <div className="benchmark-panel__section">
        <div className="benchmark-panel__section-title">
          {t('common:benchmark.section.building')}
        </div>
        {isBuildingScope ? (
          <>
            <div className="benchmark-panel__section-pps">
              {formatPricePerSqft(Math.round(central))}
            </div>
            <div className="benchmark-panel__section-sub">
              {t('common:benchmark.based_on', {
                count: sample,
                name: buildingName,
                months: period,
              })}
            </div>
          </>
        ) : (
          <div className="benchmark-panel__section-empty">
            {t('common:benchmark.building_insufficient', { building: buildingName })}
          </div>
        )}
      </div>

      {/* Winning fallback-perimeter section (project group / community / zone) */}
      {!isBuildingScope && (
        <div className="benchmark-panel__section">
          <div className="benchmark-panel__section-title">
            {isProjectScope && t('common:benchmark.section.project')}
            {isCommunityScope && t('common:benchmark.section.community')}
            {isZoneScope && t('common:benchmark.section.zone')}
          </div>
          <div className="benchmark-panel__section-pps">
            {formatPricePerSqft(Math.round(central))}
          </div>
          <div className="benchmark-panel__section-sub">
            {t('common:benchmark.based_on', { count: sample, name: winningName, months: period })}
          </div>
        </div>
      )}

      {/* Detail rows for the winning cohort */}
      <BenchmarkRow
        label={t('common:benchmark.rows.dld_reference')}
        value={formatAed(est)}
        tone="data"
      />
      <BenchmarkRow
        label={t('common:benchmark.rows.asking_price')}
        value={formatAed(askingPrice)}
      />
      <BenchmarkRow
        label={t('common:benchmark.rows.market_discount')}
        value={discountText}
        tone={discountTone}
      />
      <BenchmarkRow
        label={t('common:benchmark.rows.price_per_sqft')}
        value={t('common:benchmark.rows.price_per_sqft_vs', {
          deal: formatNumber(Math.round(dealPps)),
          market: formatNumber(Math.round(central)),
        })}
      />

      <DistributionChart
        min={data.min_price_per_sqft ?? dealPps}
        p25={data.p25_price_per_sqft ?? dealPps}
        p75={data.p75_price_per_sqft ?? dealPps}
        max={data.max_price_per_sqft ?? dealPps}
        central={central}
        deal={dealPps}
        displayStat={data.display_stat ?? 'median'}
      />

      <ComparableTransactionsTable transactions={data.comparable_transactions} />

      {/* Confidence line (ADR-055) — level + exact-perimeter qualifier */}
      <div className="benchmark-panel__confidence-line">
        {t('common:benchmark.confidence_label')}
        {' · '}
        {t(confidenceKey)}
        {' — '}
        {t(CONFIDENCE_DETAIL_KEY[family])}
      </div>

      {/* Transparency note when the comparison is a fallback perimeter */}
      {isCommunityScope && (
        <div className="benchmark-panel__warning" role="note">
          {t('common:benchmark.warning.community_fallback')}
        </div>
      )}
      {isZoneScope && (
        <div className="benchmark-panel__warning" role="note">
          {t('common:benchmark.warning.zone_fallback')}
        </div>
      )}

      <div className="benchmark-panel__footer">
        <span>{t(footerStatKey, { months: period })}</span>
        <span className="benchmark-panel__footer-dot" aria-hidden="true">
          ·
        </span>
        <span>{t('common:benchmark.footer.source')}</span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Row helper
// ---------------------------------------------------------------------------

type BenchmarkRowProps = {
  label: string;
  value: ReactNode;
  tone?: 'positive' | 'data';
};

function BenchmarkRow({ label, value, tone }: BenchmarkRowProps) {
  return (
    <div className="benchmark-panel__row">
      <span className="benchmark-panel__label">{label}</span>
      <span
        className={[
          'benchmark-panel__value',
          tone === 'positive' ? 'benchmark-panel__value--positive' : null,
          tone === 'data' ? 'benchmark-panel__value--data' : null,
        ]
          .filter(Boolean)
          .join(' ')}
      >
        {value}
      </span>
    </div>
  );
}
